/**
 * LoginPage Component
 * 
 * Displays login/register form for user authentication.
 * Matches the app's red theme (#9c0d21).
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackLogin, trackSignUp } from '../services/analytics';
import './LoginPage.css';

const LoginPage = () => {
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [voornaam, setVoornaam] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');

  const { login, register, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!email || !password) {
      setError('Vul alle velden in');
      return;
    }

    if (isRegisterMode && (!voornaam.trim() || !achternaam.trim())) {
      setError('Vul voornaam en achternaam in');
      return;
    }

    if (isRegisterMode && (voornaam.trim().length < 2 || achternaam.trim().length < 2)) {
      setError('Voornaam en achternaam moeten minimaal 2 tekens bevatten');
      return;
    }

    if (isRegisterMode && password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    if (password.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten');
      return;
    }

    try {
      setLoading(true);
      if (isRegisterMode) {
        await register(email, password, {
          voornaam: voornaam.trim(),
          achternaam: achternaam.trim()
        });
        trackSignUp();
      } else {
        await login(email, password);
        trackLogin();
      }
    } catch (err) {
      console.error('Auth error:', err);
      // Translate Firebase error messages to Dutch
      switch (err.code) {
        case 'auth/user-not-found':
          setError('Gebruiker niet gevonden');
          break;
        case 'auth/wrong-password':
          setError('Onjuist wachtwoord');
          break;
        case 'auth/email-already-in-use':
          setError('Dit e-mailadres is al in gebruik');
          break;
        case 'auth/invalid-email':
          setError('Ongeldig e-mailadres');
          break;
        case 'auth/weak-password':
          setError('Wachtwoord is te zwak');
          break;
        case 'auth/invalid-credential':
          setError('Ongeldige inloggegevens');
          break;
        default:
          setError('Er is een fout opgetreden. Probeer het opnieuw.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetSuccess('');

    if (!email) {
      setError('Vul je e-mailadres in');
      return;
    }

    try {
      setLoading(true);
      await resetPassword(email);
      setResetSuccess('Een e-mail om je wachtwoord opnieuw in te stellen is verzonden. Controleer je inbox.');
      setEmail('');
    } catch (err) {
      console.error('Reset password error:', err);
      switch (err.code) {
        case 'auth/user-not-found':
          setError('Gebruiker niet gevonden');
          break;
        case 'auth/invalid-email':
          setError('Ongeldig e-mailadres');
          break;
        default:
          setError('Er is een fout opgetreden. Probeer het opnieuw.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode);
    setIsResetMode(false);
    setError('');
    setResetSuccess('');
    setConfirmPassword('');
    setVoornaam('');
    setAchternaam('');
  };

  const toggleResetMode = () => {
    setIsResetMode(!isResetMode);
    setIsRegisterMode(false);
    setError('');
    setResetSuccess('');
    setConfirmPassword('');
    setVoornaam('');
    setAchternaam('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1 className="login-title">Etappes en Catecheses</h1>
        <h2 className="login-subtitle">
          {isResetMode ? 'Wachtwoord resetten' : isRegisterMode ? 'Account aanmaken' : 'Inloggen'}
        </h2>

        <form onSubmit={isResetMode ? handleResetPassword : handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          {resetSuccess && <div className="login-success">{resetSuccess}</div>}

          {isRegisterMode && !isResetMode && (
            <>
              <div className="form-group">
                <label htmlFor="voornaam">Voornaam</label>
                <input
                  type="text"
                  id="voornaam"
                  value={voornaam}
                  onChange={(e) => setVoornaam(e.target.value)}
                  placeholder="Jan"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="achternaam">Achternaam</label>
                <input
                  type="text"
                  id="achternaam"
                  value={achternaam}
                  onChange={(e) => setAchternaam(e.target.value)}
                  placeholder="Janssen"
                  disabled={loading}
                />
              </div>
            </>
          )}
          
          <div className="form-group">
            <label htmlFor="email">E-mailadres</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@voorbeeld.nl"
              disabled={loading}
            />
          </div>

          {!isResetMode && (
            <div className="form-group">
              <label htmlFor="password">Wachtwoord</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          )}

          {isRegisterMode && !isResetMode && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Bevestig wachtwoord</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Bezig...' : isResetMode ? 'Reset versturen' : isRegisterMode ? 'Registreren' : 'Inloggen'}
          </button>
        </form>

        {!isResetMode && !isRegisterMode && (
          <div className="login-forgot">
            <button type="button" onClick={toggleResetMode} disabled={loading}>
              Wachtwoord vergeten?
            </button>
          </div>
        )}

        <div className="login-toggle">
          {isResetMode ? (
            <p>
              Terug naar{' '}
              <button type="button" onClick={toggleResetMode} disabled={loading}>
                Inloggen
              </button>
            </p>
          ) : isRegisterMode ? (
            <p>
              Heb je al een account?{' '}
              <button type="button" onClick={toggleMode} disabled={loading}>
                Inloggen
              </button>
            </p>
          ) : (
            <p>
              Nog geen account?{' '}
              <button type="button" onClick={toggleMode} disabled={loading}>
                Registreren
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
